import { useMemo } from "react";
import { useSitesIntersectingQuery } from "@gql/li-api/graphql";
import { routedClient } from "react-migration/lib/persistence/apollo";
import type { SelectionGeometry } from "src/js/stores/map/store";
import type { Polygon, MultiPolygon } from "src/js/util/turf";
import buffer from "@turf/buffer";

export const useIntersectingSites = (geometry?: SelectionGeometry | null) => {
  const bufferMeters = -1; // negative buffer of 1 metre
  const bufferedGeometry = useMemo(
    () => (geometry ? buffer(geometry, bufferMeters, { units: "meters" }) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geometry]
  );

  const { data, ...queryRest } = useSitesIntersectingQuery({
    variables: {
      geometry: bufferedGeometry?.geometry,
      includeHidden: true,
      limit: 1000,
    },
    fetchPolicy: "cache-and-network",
    client: routedClient,
    skip: !geometry?.type,
  });

  const sites = useMemo(() => {
    if (!data?.sitesIntersectingPrecise?.edges) return [];
    return data.sitesIntersectingPrecise.edges
      .filter((edge) => edge.node.geometry && edge.node._stage)
      .map((edge) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { __typename, ...geometry } = edge.node.geometry!;
        return {
          type: "Feature" as const,
          id: edge.node._id,
          geometry: geometry as Polygon | MultiPolygon,
          properties: {
            _stage: edge.node._stage!,
            hidden: edge.node.hidden ?? false,
            title: edge.node.title ?? "",
            shared: !!edge.node.sharedByUser,
            color: edge.node._stage!.color ?? "",
            type: "Site" as const,
          },
        };
      });
  }, [data]);

  return { sites, ...queryRest };
};
